export const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    apply: 'Apply',
    close: 'Close',
    back: 'Back',
    verify: 'Verify',
    loading: 'Loading...',
    retry: 'Try Again',
    or: 'or',
    today: 'Today',
    doctor: 'Doctor',
    clinic: 'Clinic',
    unknown: 'Unknown',
    copy: 'Copy',
    copied: 'Copied!',
    add: 'Add',
  },
  language: {
    label: 'Interface language',
    english: 'English',
    indonesian: 'Bahasa Indonesia',
  },
  header: {
    sessions: 'Sessions',
    back: 'Back to sessions',
    logout: 'Log out',
    history: 'History',
    chatWithEhr: 'Chat with EHR',
    dashboard: 'Dashboard',
    clinic: 'Clinic',
    aiUsage: 'AI Usage',
    profile: 'Profile',
    endSession: 'End Session',
    stopViewing: 'Stop Viewing',
    actingAs: 'Acting as {name} · {clinic}',
    impersonation: '(Impersonation)',
    viewingClinic: 'Viewing: {clinic}',
    readOnly: '(Read-Only)',
    logoAlt: 'AIGP Health Logo',
    pages: {
      consultTitle: 'Patient Consultation',
      consultSubtitle: 'AI scribe and clinical documentation',
      dashboardTitle: 'Doctor Dashboard',
      dashboardSubtitle: 'Welcome back, {name}',
      sessionsTitle: 'Manage Sessions',
      sessionsSubtitle: 'Create and manage patient consultation sessions',
      meetingTitle: 'Online Consultation',
      meetingSubtitle: 'Virtual meeting room for patient consultations',
      profileTitle: 'My Profile',
      profileSubtitle: 'Manage your account information and preferences',
      clinicProfileTitle: 'Clinic Profile',
      clinicProfileSubtitle: 'Manage your clinic information and settings',
      clinicAdminTitle: 'Clinic Administration',
      clinicAdminSubtitle: 'Welcome, {name}',
      adminTitle: 'Admin Dashboard',
      adminSubtitle: 'Welcome back, {name}',
      superAdminTitle: 'Super Admin Dashboard',
      superAdminSubtitle: 'Welcome, {name}',
      aiUsageTitle: 'AI Usage',
      aiUsageSubtitle: 'Token consumption, cost and latency across AI flows',
      defaultTitle: 'AIGP Health',
      defaultSubtitle: 'Medical Consultation Platform',
      adminFallback: 'Admin',
      superAdminFallback: 'Super Admin',
    },
  },
  auth: {
    welcomeBack: 'Welcome back',
    loginSubtitle: 'Sign in to continue to your AIGP Health workspace.',
    signInEyebrow: 'Sign in',
    signInTitle: 'Sign in to your account',
    email: 'Email address',
    emailPlaceholder: 'you@clinic.com',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signInGoogle: 'Sign in with Google',
    forgotPassword: 'Forgot password?',
    createAccount: 'Create an account',
    verifyEyebrow: 'Verify identity',
    enterCode: 'Enter your code',
    otpHint: 'Enter the 6-digit code sent to your registered phone via WhatsApp.',
    loginOtpAria: 'Login verification code',
    resendIn: 'Resend code in {seconds}s',
    requestNewCode: 'Request a new code',
    secureEyebrow: 'Secure your account',
    mfaTitle: 'Set up two-factor authentication',
    mfaHint: 'Add a phone number to receive one-time codes via WhatsApp.',
    phone: 'Phone number',
    phonePlaceholder: 'Enter phone number',
    sendCode: 'Send code',
    resendCode: 'Resend code',
    mfaOtpAria: 'MFA setup verification code',
    invalidEmail: 'Please enter a valid email address.',
    enterPassword: 'Please enter your password.',
    emailUnverified: 'Your email is not verified yet. Please verify it before setting up MFA.',
    authError: 'Authentication error. Please contact support.',
    badCredentials: 'Incorrect email or password. Please try again.',
    unableToSignIn: 'Unable to sign in. Please try again.',
    enterFullCode: 'Please enter the full 6-digit code.',
    sessionExpired: 'Session expired. Please sign in again.',
    invalidCode: 'Invalid code. Please try again.',
    couldNotResend: 'Could not resend the code.',
    startOver: 'Start over',
    attemptRemaining: '{count} attempt remaining',
    attemptsRemaining: '{count} attempts remaining',
    invalidPhone: 'Please enter a valid phone number.',
    couldNotSend: 'Could not send the code.',
    googleUnexpected: 'Unexpected response from Google sign-in.',
    googleFailed: 'Google sign-in failed.',
    routingFailed: 'Login completed but routing failed. Please retry.',
    shellDefaultTitle: 'Welcome to AIGP Health',
    shellDefaultSubtitle: 'Evidence-based clinical decision support, built for rheumatology and general practice.',
    trustHipaa: 'HIPAA-aligned data handling',
    trustEncrypted: 'Encrypted in transit and at rest',
    trustEvidence: 'Clinician-verified evidence',
    registrationPhases: 'Registration phases',
    trustAria: 'Trust and security',
    progress: 'Progress',
    disclaimerTitle: 'Disclaimer',
    disclaimerBody:
      'AIGP Health and its AI platform "Anzu" are intended to assist — not replace — the clinical judgment of qualified healthcare professionals. All clinical decisions must be made by a licensed medical practitioner. The information provided by Anzu is not a substitute for professional medical advice, diagnosis, or treatment.',
    iUnderstand: 'I Understand',
  },
  forgot: {
    title: 'Reset your password',
    subtitle: "We'll help you get back into your account.",
    eyebrow: 'Account recovery',
    stepEmail: 'Reset password',
    stepOtp: 'Verify code',
    stepReset: 'Set new password',
    hintEmail: 'Enter your email and we will send a reset code.',
    hintOtp: 'Enter the 6-digit code sent to your phone.',
    hintReset: 'Enter the token from your email and choose a new password.',
    sendResetCode: 'Send reset code',
    otpAria: 'Password reset code',
    resetToken: 'Reset token',
    tokenPlaceholder: 'Paste the token from your email',
    newPassword: 'New password',
    newPasswordPlaceholder: 'Enter a new password',
    confirmPassword: 'Confirm new password',
    confirmPlaceholder: 'Re-enter your new password',
    resetPassword: 'Reset password',
    backToSignIn: 'Back to sign in',
    codeSent: 'A reset code has been sent to your phone.',
    codeResent: 'A new reset code has been sent.',
    sendFailed: 'Failed to send code. Please try again.',
    resendFailed: 'Failed to resend code.',
    enterFullCode: 'Please enter the full 6-digit code.',
    tokenSent: 'Reset token sent to your email. Enter it below with your new password.',
    invalidCode: 'Invalid code. Please try again.',
    enterToken: 'Please enter the reset token from your email.',
    passwordRequirements: 'Password does not meet the requirements.',
    passwordsMismatch: 'Passwords do not match.',
    resetSuccess: 'Password reset successfully. Redirecting to sign in…',
    resetFailed: 'Failed to reset password. Please try again.',
  },
  dashboard: {
    approvalTitle: 'Clinic Approval Pending',
    approvalBody: 'Your clinic registration is currently under review by our administration team.',
    approvalAccess: 'You will be able to access the doctor portal once your clinic has been approved.',
    approvalHelp: 'Please contact your clinic administrator or support if you have any questions.',
    welcome: 'Good day, Dr. {name}!',
    welcomeBody: 'Ready to manage your patient sessions and provide excellent care',
    whatToDo: 'What would you like to do?',
    manageSessions: 'Manage Sessions',
    manageSessionsBody: 'Create, edit, and organize patient consultation sessions',
    manageFeature1: 'Schedule new consultations',
    manageFeature2: 'Update patient information',
    manageFeature3: 'Manage appointment details',
    clinicAdmin: 'Clinic Administration',
    clinicAdminBody: 'Manage users, subscriptions, and clinic overview',
    clinicFeature1: 'Overview & analytics',
    clinicFeature2: 'User management',
    clinicFeature3: 'Subscription management',
  },
  usage: {
    loadingChip: 'Sessions…',
    loadingBanner: 'Loading your session usage…',
    loadingTitle: 'Loading session usage',
    errorChip: 'Sessions —',
    errorBanner: 'Couldn’t load your session usage. {error}',
    loadFailed: 'Failed to load session usage',
    unlimitedTitle: 'Unlimited sessions — no monthly cap',
    usedTitle: '{used} of {limit} sessions used this month',
    unlimitedChip: 'Sessions ∞',
    sessionsLabel: 'sessions',
    unlimitedBanner: 'Unlimited — no monthly cap.',
    usedThisPeriod: 'You’ve used {used} session this period.',
    usedThisPeriodPlural: 'You’ve used {used} sessions this period.',
    reachedLimit: 'You’ve reached your monthly session limit ({used}/{limit}). Sessions reset next period.',
    oneLeft: '1 session left this month. {used}/{limit} used.',
    manyLeft: '{remaining} sessions left this month. {used}/{limit} used.',
  },
  inactivity: {
    title: 'Session Expiring Soon',
    body: 'Your session will expire due to inactivity in:',
    continueQuestion: 'Would you like to continue your session?',
    logoutNow: 'Logout Now',
    continueSession: 'Continue Session',
  },
  subscription: {
    title: 'Subscription Pending',
    bodyNamed: "Dr. {name}, your account is set up, but your clinic doesn't have an active subscription yet.",
    bodyUnnamed: "Your account is set up, but your clinic doesn't have an active subscription yet.",
    access: "You'll be able to access the doctor portal as soon as your clinic is on a plan.",
    help: 'Only your clinic administrator can activate the subscription. Once they do, check again below or sign in again.',
    checking: 'Checking…',
    checkAgain: 'Check again',
    stillPending: "Your clinic still doesn't have an active subscription. Please check with your clinic administrator.",
    checkFailed: 'Could not check subscription status. Please try again shortly.',
  },
  sessions: {
    tabSessions: 'Sessions',
    tabSignature: 'Signature',
    tabMedicationCatalog: 'Medication Catalog',
    tabProtocols: 'Protocols',
    tabEducation: 'Education Content',
    tabScheduling: 'Scheduling',
    noSignature: 'No signature set',
    filterToday: 'Today',
    filterTomorrow: 'Tomorrow',
    filterThisWeek: 'This Week',
    filterAll: 'All',
    filterCustom: 'Custom Range',
    listView: 'List view',
    calendarView: 'Calendar view',
    allDoctors: 'All Doctors',
    sortBy: 'Sort by:',
    sortDefault: 'Default (newest first)',
    sortDateNewest: 'Appointment date — newest first',
    sortDateOldest: 'Appointment date — oldest first',
    sortTimeEarliest: 'Appointment time — earliest first',
    sortTimeLatest: 'Appointment time — latest first',
    sortIdLow: 'Session ID — low to high',
    sortIdHigh: 'Session ID — high to low',
    searchPlaceholder: 'Search by patient name or phone...',
    bulkUpload: 'Bulk Upload',
    createNew: 'Create New Session',
    loading: 'Loading sessions...',
    appointmentsHeading: '{filter} Appointments',
    appointmentsRange: '{start} - {end} Appointments',
    emptyTitle: 'No {filter} appointments found',
    emptySearch: 'No {filter} appointments match your search criteria.',
    emptyScheduled: 'No appointments scheduled for {filter}.',
    emptyRange: 'No appointments found for {start} - {end}',
    emptyRangeSearch: 'No appointments in the selected date range match your search criteria.',
    emptyAll: 'No sessions available. Create your first session to get started.',
    emptyRangeScheduled: 'No appointments scheduled for the selected date range.',
    emptyPickRange: 'Please select a date range to view appointments.',
    showingRange: 'Showing {start}-{end} of {total}',
    colPatient: 'Patient Name',
    colPatientHint: 'Use search to find a patient',
    colPhone: 'Phone Number',
    colDoctor: 'Doctor',
    colNotification: 'Notification',
    colPreconsult: 'Pre consultation',
    colType: 'Session Type',
    sent: 'Sent',
    notSent: 'Not Sent',
    rowsPerPage: 'Rows per page:',
    statusNotArrived: 'Not Arrived',
    statusCheckedIn: 'Checked In',
    statusInConsultation: 'In Consultation',
    statusCompleted: 'Session Completed',
    statusCompletedShort: 'Completed',
    statusNoShow: 'No Show',
    statusCancelled: 'Cancelled',
    createTitle: 'Create New Session',
    searchMrn: 'Search by MRN',
    searchMrnPlaceholder: 'Enter last 4+ digits of MRN to search',
    searching: 'Searching...',
    patientName: 'Patient Name *',
    patientNamePlaceholder: "Enter patient's full name",
    phone: 'Phone Number *',
    phonePlaceholder: 'Enter phone number',
    mrnOptional: 'Medical Record Number (optional)',
    mrnPlaceholder: 'Enter clinic MRN if available',
    dob: 'Date of Birth *',
    dobRequired: 'Date of birth is required',
    dobMissingOnRecord:
      'This patient record has no date of birth. Add it to create the session.',
    patientMatchHint:
      'Type at least two letters of the name to see existing patients.',
    matchingPatients: 'Existing patients that match',
    sameNameWarning:
      'Check: more than one record shares this name. Confirm with the phone digits or date of birth before choosing.',
    noneOfTheseNewPatient: 'None of these — continue as a new patient',
    sendLinkHint: 'Send the pre-consultation link',
    resendLinkHint: 'Send a new pre-consultation link',
    sendLinkTitle: 'Send pre-consultation link?',
    resendLinkTitle: 'Send a new pre-consultation link?',
    resendLinkBody: 'A pre-consultation link will be sent by WhatsApp to {name} ({phone}).',
    resendLinkSupersedes: 'The link sent earlier will stop working as soon as the new one is sent.',
    resendLinkWindow: 'The link is timed to the visit: it opens two days before the appointment and stops working a few hours after it starts.',
    resendLinkConfirm: 'Send link',
    resendLinkSent: 'Pre-consultation link sent to {name}. Any earlier link no longer works.',
    resendLinkAlreadyDone: 'No link sent: this patient has already done the pre-consultation for this session.',
    resendLinkFailed: 'The pre-consultation link could not be sent.',
    assignDoctor: 'Assign Doctor *',
    selectDoctor: 'Select a doctor',
    appointmentDate: 'Appointment Date *',
    appointmentTime: 'Appointment Time *',
    sessionType: 'Session Type *',
    typePhysical: 'Physical',
    typeOnline: 'Online',
    practitionerType: 'Practitioner Type *',
    patientLanguage: 'Patient language',
    rheumatologist: 'Rheumatologist',
    dermatologist: 'Dermatologist',
    generalPractitioner: 'General Practitioner',
    viewTitle: 'View Session Details',
    viewConfirm: 'View Session',
    viewSubtitle: "You're about to view session information",
    sessionInformation: 'Session Information',
    sessionId: 'Session ID',
    patient: 'Patient',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    editAppointment: 'Edit Appointment',
    selectDateRange: 'Select Date Range',
    from: 'From:',
    to: 'To:',
    bulkTitle: 'Bulk Upload Appointments',
    csvRequirements: 'CSV File Requirements:',
    csvNoHeaders: 'File must be in CSV format (.csv) without headers',
    csvMaxSize: 'Maximum file size: 5MB',
    dropCsv: 'Click to select CSV file or drag and drop',
    maxSizeHint: 'Max file size: 5MB',
    bulkSummary: 'Bulk Upload Summary',
    totalProcessed: 'Total Processed',
    successful: 'Successful',
    failed: 'Failed',
    skipped: 'Skipped',
    ignored: 'Ignored',
    issues: 'Issues Encountered:',
    downloadFailed: 'Download Failed Records CSV',
    calToday: 'Today',
    patientUnavailable: 'Patient unavailable',
    unassigned: 'Unassigned',
    notSet: 'Not set',
    sessionCalendar: 'Session Calendar',
    colAppointmentDate: 'Appointment Date',
    colAppointmentTime: 'Appointment Time',
    clickToView: 'Click to view session details',
    editAppointmentHint: 'Edit appointment date/time',
    sessionCount: '{count} session',
    sessionCountPlural: '{count} sessions',
    weekdayMon: 'Mon',
    weekdayTue: 'Tue',
    weekdayWed: 'Wed',
    weekdayThu: 'Thu',
    weekdayFri: 'Fri',
    weekdaySat: 'Sat',
    weekdaySun: 'Sun',
    loadFailed: 'Failed to load sessions. Please try again.',
    navigateFailed: 'Failed to navigate to session. Please try again.',
    appointmentUpdated: 'Appointment date/time updated for session #{id}',
    updateAppointmentFailed: 'Failed to update appointment. Please try again.',
    dateRequired: 'Appointment date is required',
    datePast: 'Appointment date cannot be in the past (Singapore time)',
    timeRequired: 'Appointment time is required',
    nameRequired: 'Patient name is required',
    nameInvalid: 'Please enter a valid name (2-50 characters)',
    phoneRequired: 'Phone number is required',
    phoneInvalid: 'Please enter a valid phone number',
    doctorRequired: 'Please select a doctor to assign',
    sessionCreated: 'Session created successfully! Session ID: {id}',
    createFailed: 'Failed to create session. Please try again.',
    statusUpdated: 'Appointment status updated to "{status}"',
    statusUpdateFailed: 'Failed to update appointment status. Please try again.',
    maxRows: 'Maximum {max} rows per page.',
    selectValidCsv: 'Please select a valid CSV file',
    fileTooLarge: 'File size must be less than 5MB',
    selectCsv: 'Please select a CSV file',
    uploadFailed: 'Failed to upload CSV file. Please try again.',
    dropValidCsv: 'Please drop a valid CSV file',
    csvColumnOrder: 'Required column order (15 columns):',
    downloadSample: 'Download Sample CSV',
    changeFile: 'Change File',
    uploading: 'Uploading...',
    uploadCsv: 'Upload CSV',
    failedRecords: 'Failed Records ({count})',
    skippedRecords: 'Skipped Records ({count})',
    ignoredRecords: 'Ignored Records ({count})',
    rowLabel: 'Row {row}',
    unknownError: 'Unknown error',
    unknownReason: 'Unknown reason',
    moreFailed: '...and {count} more failed records',
    moreSkipped: '...and {count} more skipped records',
    moreIgnored: '...and {count} more ignored records',
  },
  consult: {
    readyTitle: 'Ready to transcribe',
    readyBody: 'Start recording to capture the consultation',
    consultLanguage: 'Consultation language',
    transcriptionLanguage: 'Consultation Language',
    outputLanguage: 'Notes Language',
    outputLanguageHint: 'AI notes and analysis will be written in the notes language',
    consultLanguageLocked: 'Not included in your plan',
    startRecording: 'Start Recording',
    uploadWav: 'Upload WAV (Dev)',
    uploading: 'Uploading recording...',
    transcribing: 'Transcribing audio...',
    analyzing: 'Analyzing consultation...',
    complete: 'Complete!',
    processing: 'Processing...',
    uploadingHint: 'Sending audio to server',
    transcribingHint: 'Converting speech to text',
    analyzingHint: 'Generating clinical insights',
    completeHint: 'Finishing up',
    processingHint: 'Please wait',
    initializing: 'Initializing...',
    initializingHint: 'Setting up audio connection',
    recording: 'Recording',
    paused: 'Paused',
    resume: 'Resume',
    pause: 'Pause',
    stop: 'Stop',
    recordingPaused: 'Recording paused',
    recordingAudio: 'Recording consultation audio...',
    stopWhenFinished: 'Click Stop when finished',
    stopped: 'Stopped',
    startNewRecording: 'Start New Recording',
    recordingComplete: 'Recording complete',
    insertHint: 'Click "Insert to Notes" to transcribe and analyze',
    insertToNotes: 'Insert to Notes',
    consentTitle: 'Patient Consent Required',
    consentBody:
      'Before starting the recording session for medical transcription, please confirm that you have obtained proper consent from the patient.',
    consentCheckbox:
      'I confirm that I have gathered proper consent from the patient for audio recording and transcription of this medical consultation.',
    acknowledge: 'Acknowledge',
    startTitle: 'Start Recording',
    startBody: 'Are you sure you want to start recording? This will capture audio from your microphone for transcription.',
    yesStart: 'Yes, Start Recording',
    privateNotes: 'Private Notes',
    saving: '(Saving...)',
    privateNotesPlaceholder: 'Add private notes about this patient...',
    visitsTitle: 'All Visits Timeline',
    loadingHistory: 'Loading patient history...',
    sessionNumber: 'Session #{id}',
    appointmentTime: 'Appointment time: {time}',
    noPreviousVisits: 'No previous visits found for this patient.',
    languageUpdateFailed: 'Failed to update consultation language',
    inConsultSaved: 'In-consult data saved',
    inConsultSaveFailed: 'Failed to save in-consult data',
    signedOff: 'Session signed off and ended.',
    endFailed: 'Failed to end session. Please try again.',
    noAudio: 'No audio recorded',
    analysisFailed: 'Analysis failed after retries — please retry',
    transcribed: 'Recording transcribed and analyzed',
    processFailed: 'Failed to process recording',
    recovered: 'Recovered recording transcribed and analyzed',
    recoverFailed: 'Failed to recover recording',
    wavDone: 'WAV file transcribed and analyzed',
    wavFailed: 'Failed to process WAV upload',
    alreadySignedOff: 'Session already signed off',
    alreadySignedOffBody:
      "This session has been signed off, so its medications are approved. Re-running the scribe may revise or duplicate approved prescriptions. Continue only if you intend to update this consultation's records.",
    rerunScribe: 'Re-run scribe',
    endTitle: 'End session and sign off?',
    endBody: 'This finalises the consultation and pushes the note to the clinic system.',
    endConfirm: 'End session',
    sectionLoading: 'Analyzing...',
    sectionFailed: 'Analysis failed, showing previous notes',
    analyzingProgressive: 'Building notes section by section...',
    analyzingProgressiveHint: 'Each section appears as soon as its analysis is ready',
    runInProgress: 'Scribe analysis is still running. Please wait for all sections to finish.',
    scribeAgents: {
      history: 'History',
      examination: 'Examination',
      diagnosis: 'Diagnosis',
      medication: 'Medications',
      eq: 'Rapport reminders',
      followup: 'Follow-up',
    },
  },
  workspace: {
    tabs: {
      preClerk: 'Pre-Clerk',
      inConsult: 'In-Consult',
      workflow: 'Workflow',
      sessionSummary: 'Session Summary',
      chroniq: 'Chroniq',
      clinicalSummary: 'Clinical Summary',
      careGaps: 'Care gaps',
    },
    careGaps: {
      title: 'Care gaps',
      subtitle: 'Target vs. current disease activity, from signed-off visits',
      ruleset: 'Ruleset {version}',
      openCount: '{count} open',
      onTarget: 'On target',
      reevaluate: 'Re-evaluate',
      reevaluating: 'Evaluating…',
      loading: 'Checking care gaps…',
      empty: 'On target. No open care gaps.',
      silent: 'The engine is evaluating silently. Gaps will appear here once display is switched on for this clinic.',
      showHistory: 'Show {count} resolved or closed',
      hideHistory: 'Hide resolved and closed',
      recorded: 'Recorded',
      noScore: 'No activity score recorded',
      targetNotMet: 'Target not met',
      targetMet: 'Target met',
      targetUnknown: 'Target: unable to determine',
      action: 'Suggested action',
      due: 'Due {date}',
      closedOn: 'Closed {date}',
      close: 'Close gap',
      exception: 'Add exception',
      rule: {
        measurementMissing: 'No disease activity score recorded',
        aboveTarget: 'Disease activity above target',
      },
      disease: { ra: 'RA', psa: 'PsA', axspa: 'axSpA', sle: 'SLE' },
      status: {
        open: 'Open',
        resolved: 'Resolved',
        excepted: 'Excepted',
        closed: 'Closed',
        withdrawn: 'Withdrawn',
      },
      dialog: {
        closeTitle: 'Close care gap',
        closeHint: 'Record the evidence that the closure criterion is met.',
        evidenceLabel: 'Closure evidence',
        evidencePlaceholder: 'e.g. CDAI 8 recorded today; target reached',
        confirmClose: 'Close gap',
        exceptionTitle: 'Record an exception',
        exceptionHint: 'An individualised target or a documented reason to accept the current activity. The gap stays suppressed until the exception expires.',
        rationaleLabel: 'Rationale',
        rationalePlaceholder: 'e.g. Pregnancy: treat-to-target paused; review at 20 weeks',
        expiresLabel: 'Review by',
        expiresHint: 'Must be a future date, at most 12 months away.',
        confirmException: 'Save exception',
        cancel: 'Cancel',
        saving: 'Saving…',
      },
      errors: {
        displayOff: 'Care gaps are not switched on for this clinic yet.',
        evidenceRequired: 'Closure evidence is required.',
        rationaleRequired: 'A rationale is required.',
        notFound: 'This care gap is no longer open.',
        invalidException: 'The exception must expire on a future date within 12 months.',
        generic: 'Could not update care gaps. Please try again.',
      },
    },
    subTabs: {
      history: 'History (Hx)',
      exam: 'Exam',
      dx: 'Dx/Assessment',
      plan: 'Plan / Mx',
      billing: 'Billing & Orders',
      pro: 'PRO',
      aiScribe: 'AI Scribe',
    },
    headings: {
      practitionerNotes: 'Practitioner Notes',
      practitionerNotesEmpty: 'No practitioner notes. Click to add.',
      practitionerNotesPlaceholder: 'Click to add notes...',
      hpi: 'History of Present Illness',
      hpiPlaceholder: 'Enter history of present illness...',
      hpiEmpty: 'No history of present illness recorded. Click to add.',
      ros: 'Review of Systems',
      rosPlaceholder: 'Enter review of systems (e.g., Constitutional: Denies fever...)',
      rosEmpty: 'No review of systems recorded. Click to add.',
      pastMedicalHx: 'PAST MEDICAL HX',
      pastMedicalHxPlaceholder: 'Enter past medical history...',
      pastMedicalHxEmpty: 'No past medical history. Click to add.',
      pastSurgicalHx: 'PAST SURGICAL HX',
      pastSurgicalHxPlaceholder: 'Enter past surgical history...',
      pastSurgicalHxEmpty: 'No past surgical history. Click to add.',
      familyHistory: 'FAMILY HISTORY',
      familyHistoryAdd: 'Add',
      familyHistoryAddTitle: 'Add family member',
      familyHistoryEmpty: 'No Family History',
      familyHistoryEmptyHint: 'Start by adding the patient (proband) to the family tree.',
      familyHistoryStart: 'Start Family Tree',
      familyHistoryExpanded: 'Family History - Expanded View',
      familyHistoryMale: 'Male',
      familyHistoryFemale: 'Female',
      familyHistoryAffected: 'Affected',
      familyHistoryDeceased: 'Deceased',
      medsAllergies: 'MEDS & ALLERGIES',
      medications: 'Medications:',
      medicationsPlaceholder: 'Enter current medications...',
      medicationsEmpty: 'No medications recorded. Click to add.',
      allergies: 'Allergies:',
      allergiesPlaceholder: 'Enter allergies...',
      allergiesEmpty: 'No allergies recorded. Click to add.',
      socialHistory: 'SOCIAL HISTORY',
      socialHistoryPlaceholder: 'Enter social history...',
      socialHistoryEmpty: 'No social history recorded. Click to add.',
      historySection: 'HISTORY',
      rosPlaceholderBrief: 'Enter review of systems...',
      hpiEmptyBrief: 'No history of present illness recorded.',
      rosEmptyBrief: 'No review of systems recorded.',
      pastMedicalHxEmptyBrief: 'No past medical history.',
      pastSurgicalHxEmptyBrief: 'No past surgical history.',
      medicationsEmptyBrief: 'No medications recorded.',
      socialHistoryEmptyBrief: 'No social history recorded.',
      copyToClipboard: 'Copy to clipboard',
    },
    left: {
      mrn: 'MRN',
      noMrn: 'No MRN available',
      noName: 'No name available',
      ageGender: 'AGE / GENDER',
      lastVisit: 'LAST VISIT',
      noPreviousVisits: 'No previous visits',
      privateNote: 'PRIVATE NOTE',
      privateNotePlaceholder: 'Add clinical context...',
      activeProblems: 'Active Problems',
      noActiveProblems: 'No active problems available',
      activeMedications: 'Active Medications',
      noActiveMedications: 'No active medications available',
      lastInvestigation: 'Last Investigation',
      noData: 'No data available',
      moreOptions: 'More options',
      edit: 'Edit',
    },
    alerts: {
      title: 'Allergies & Alerts',
      drugAllergies: 'Drug allergies',
      otherAlerts: 'Other alerts',
      noAllergyRecord: 'No allergy record yet.',
      noKnownDrugAllergies: 'No known drug allergies',
      addAllergy: 'Add allergy',
      addAlert: 'Add alert',
      allergyPlaceholder: 'Drug and reaction, e.g. Penicillin - rash',
      alertPlaceholder: 'What the alert is',
      medicalAlert: 'Medical alert',
      anticoagulation: 'Anticoagulation',
      fromChat: 'From the pre-consult chat',
      recordedByDoctor: 'Recorded by a doctor',
      edit: 'Edit',
      remove: 'Remove',
      confirmRemove: 'Remove this entry?',
      loadFailed: 'Allergies and alerts could not be loaded.',
      saveFailed: 'Could not save: {reason}',
    },
    right: {
      timeline: 'TIMELINE',
      rx: 'RX',
      review: 'REVIEW',
      ix: 'IX',
      orders: 'ORDERS',
      quickRx: 'QUICK RX',
      letters: 'LETTERS',
      visitTimeline: 'Visit Timeline',
      loadingHistory: 'Loading patient history...',
      noPreviousVisits: 'No previous visits found.',
      sessionLabel: 'Session #{id} - {name}',
      patientFallback: 'Patient',
      currentlyViewing: 'Currently viewing',
      didNotAttend: 'Did not attend',
      completed: 'Completed',
      closed: 'Closed',
      consultationComplete: 'Consultation complete',
      inProgress: 'In Progress',
    },
    preclerk: {
      patientHistory: 'Patient History & Symptoms (AI Generated)',
      noPatientHistory: 'No patient history available',
      ros: 'Review of Systems:',
      noRos: 'No review of systems available',
      pastMedical: 'Past Medical Concerns:',
      noPastMedical: 'No past medical concerns available',
      pastSurgical: 'Past Surgical Concerns:',
      noPastSurgical: 'No past surgical concerns available',
      familyHistory: 'Family History:',
      noFamilyHistory: 'No family history available',
      drugAllergies: 'Drug & Allergies:',
      noDrugAllergies: 'No medications or allergies available',
      socialHistory: 'Social History:',
      noSocialHistory: 'No social history available',
    },
    exam: {
      msk: 'MSK',
      general: 'General',
      skinHeent: 'Skin/HEENT',
      eyes: 'Eyes',
      cvs: 'CVS',
      respiratory: 'Resp',
      abdominal: 'Abdo',
      neuro: 'Neuro',
      summary: 'Summary',
      skinExam: 'Skin exam',
      generalAppearance: 'GENERAL APPEARANCE',
      skinMucocutaneous: 'SKIN & MUCOCUTANEOUS',
      examinerSummary: "EXAMINER'S SUMMARY & IMPRESSION",
      diseaseActivity: 'DISEASE ACTIVITY SCORES & PROs',
      cardiovascular: 'CARDIOVASCULAR',
      jointExam: 'MUSCULOSKELETAL / JOINT EXAMINATION',
      neurological: 'NEUROLOGICAL',
      eyesSection: 'EYES',
      respiratorySection: 'RESPIRATORY',
      abdominalSection: 'ABDOMINAL',
      lymphoreticular: 'LYMPHORETICULAR & SALIVARY',
    },
    dx: {
      section: 'DX / ASSESSMENT',
      primary: 'PRIMARY DIAGNOSIS',
      principal: 'Principal Problem',
      acute: 'Acute Diagnoses',
      chronic: 'Chronic Diagnoses',
      differential: 'Differential Diagnoses Considered',
      clinicalReasoning: 'Clinical Reasoning',
      addDiagnosis: 'Add Diagnosis',
      addAcute: 'Add Acute Diagnosis',
      addChronic: 'Add Chronic Diagnosis',
      addDifferential: 'Add Differential Diagnosis',
      noPrimary: 'No primary diagnosis available',
      noAcute: 'No acute diagnoses',
      noChronic: 'No chronic diagnoses',
      noDifferential: 'No differential diagnoses considered',
      insights: 'Clinical Insights from Guidelines',
      insightsUpper: 'CLINICAL INSIGHTS FROM GUIDELINES',
    },
    ehrChat: {
      title: 'Chat with EHR',
      ready: 'AI assistant ready',
      unavailable: 'Unavailable',
      initializing: 'Initializing...',
      close: 'Close chat',
      connecting: 'Connecting to EHR...',
      medicationHistory: 'Medication history',
      medicationHistoryQuery: 'Show medication history',
      recentLabs: 'Recent labs',
      recentLabsQuery: 'Summarize recent labs',
      previousVisits: 'Previous visits',
      previousVisitsQuery: 'Show previous visits',
      placeholder: 'Ask about patient data, medications, results...',
      send: 'Send message',
    },
  },
} as const;
